import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Languages, Send, MessageCircle } from 'lucide-react';
import { API } from '../utils/apiConfig';

/**
 * In-ride chat between driver and confirmed passengers.
 * Auto-translates incoming messages via DeepL when toggle is ON.
 *
 * Props:
 *   rideId   — ride UUID
 *   me       — { id, name, lang? }   (current user)
 *   onClose? — optional close handler for modal usage
 */
export const RideChat = ({ rideId, me, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [translateOn, setTranslateOn] = useState(false);
  const [targetLang, setTargetLang] = useState((me?.lang || 'de').slice(0, 2));
  const [translatedById, setTranslatedById] = useState({});
  const translatedRef = useRef({});
  // Keep ref in sync so async loop reads the latest map without retriggering the effect
  useEffect(() => { translatedRef.current = translatedById; }, [translatedById]);
  const scrollRef = useRef(null);

  const TARGET_LANGS = ['de', 'en', 'fr', 'it', 'es', 'tr', 'pl', 'uk'];

  // Load messages
  useEffect(() => {
    if (!rideId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/chat/rides/${rideId}/messages`);
        if (!cancelled) setMessages(r.data?.messages || []);
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 5000);   // light polling — socket-broadcast wires it later
    return () => { cancelled = true; clearInterval(t); };
  }, [rideId]);

  // Auto-translate incoming messages whenever toggle / target / messages change
  useEffect(() => {
    if (!translateOn) { setTranslatedById({}); return; }
    let cancelled = false;
    (async () => {
      const next = { ...translatedRef.current };
      for (const m of messages) {
        if (m.mine) continue;
        if (next[m.id]?.target === targetLang) continue;
        try {
          const r = await axios.post(`${API}/chat/rides/${rideId}/translate`, {
            text: m.text, target_lang: targetLang, source_lang: m.lang || undefined,
          });
          if (cancelled) return;
          if (r.data?.ok) next[m.id] = { translated: r.data.translated, target: targetLang };
        } catch (_) { /* skip */ }
      }
      if (!cancelled) setTranslatedById(next);
    })();
    return () => { cancelled = true; };
  }, [translateOn, targetLang, messages, rideId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const v = text.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/chat/rides/${rideId}/messages`,
        { text: v, lang: (me?.lang || 'de').slice(0, 2) });
      setText('');
      const r = await axios.get(`${API}/chat/rides/${rideId}/messages`);
      setMessages(r.data?.messages || []);
    } catch (e) {
      // Surface the failure inline
      // eslint-disable-next-line no-console
      console.error('chat send failed', e);
    }
    setBusy(false);
  };

  return (
    <div data-testid="ride-chat" style={st.container}>
      <div style={st.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageCircle size={16} color="#00d4ff" />
          <strong style={{ fontSize: 14, color: '#f1f5f9' }}>Chat</strong>
        </div>
        <div data-testid="chat-deepl-toggle" style={{
          ...st.toggle,
          background: translateOn ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.06)',
          borderColor: translateOn ? 'rgba(129,140,248,0.45)' : 'rgba(255,255,255,0.1)',
          color: translateOn ? '#c7d2fe' : 'rgba(255,255,255,0.55)',
        }}>
          <button data-testid="chat-deepl-btn" onClick={() => setTranslateOn(v => !v)}
            style={st.toggleBtn} title="DeepL Live-Übersetzung">
            <Languages size={13} /> {translateOn ? 'ON' : 'OFF'}
          </button>
          {translateOn && (
            <select data-testid="chat-deepl-target" value={targetLang}
              onChange={e => setTargetLang(e.target.value)} style={st.toggleSelect}>
              {TARGET_LANGS.map(l => (
                <option key={l} value={l} style={{ color: '#0f172a', background: '#fff' }}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} data-testid="chat-close" style={st.closeBtn}>×</button>
        )}
      </div>

      <div ref={scrollRef} data-testid="chat-messages" style={st.messages}>
        {messages.length === 0 && (
          <div style={st.empty}>Noch keine Nachrichten — schreib die erste.</div>
        )}
        {messages.map(m => (
          <div key={m.id} data-testid={`chat-msg-${m.id}`}
            style={{ ...st.msg, ...(m.mine ? st.msgMine : st.msgOther) }}>
            <div style={st.msgText}>{m.text}</div>
            {translateOn && !m.mine && translatedById[m.id]?.translated && (
              <div data-testid={`chat-msg-${m.id}-translated`} style={st.msgTranslation}>
                <Languages size={11} /> {translatedById[m.id].translated}
              </div>
            )}
            <div style={st.msgTime}>
              {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={e => { e.preventDefault(); send(); }} style={st.form}>
        <input data-testid="chat-input" type="text" value={text}
          onChange={e => setText(e.target.value)} placeholder="Nachricht…"
          style={st.input} />
        <button data-testid="chat-send" type="submit" disabled={busy || !text.trim()}
          style={{ ...st.sendBtn, opacity: text.trim() ? 1 : 0.4 }}>
          <Send size={14} />
        </button>
      </form>
    </div>
  );
};

const st = {
  container: {
    display: 'flex', flexDirection: 'column',
    width: '100%', maxWidth: 480, height: 520,
    background: 'rgba(15,23,42,0.96)', backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  toggle: {
    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
    border: '1px solid', borderRadius: 8, padding: '3px 8px', fontSize: 11,
    transition: 'all 0.2s',
  },
  toggleBtn: {
    background: 'transparent', border: 'none', color: 'inherit',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
    padding: 0, fontSize: 11,
  },
  toggleSelect: {
    background: 'transparent', border: 'none', color: '#c7d2fe',
    fontSize: 11, outline: 'none', cursor: 'pointer',
  },
  closeBtn: {
    background: 'transparent', border: 'none', color: '#94a3b8',
    fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '0 4px',
  },
  messages: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { color: '#64748b', fontSize: 13, textAlign: 'center', marginTop: 30 },
  msg: { maxWidth: '78%', padding: '8px 12px', borderRadius: 14, fontSize: 14 },
  msgMine: { alignSelf: 'flex-end', background: 'linear-gradient(135deg,#0ea5e9,#00d4ff)', color: '#fff' },
  msgOther: { alignSelf: 'flex-start', background: 'rgba(255,255,255,0.08)', color: '#f1f5f9' },
  msgText: { lineHeight: 1.4 },
  msgTranslation: {
    marginTop: 6, paddingTop: 6, fontSize: 12, color: '#a5b4fc',
    borderTop: '1px dashed rgba(165,180,252,0.3)',
    display: 'flex', alignItems: 'center', gap: 4,
  },
  msgTime: { marginTop: 4, fontSize: 10, opacity: 0.6, textAlign: 'right' },
  form: {
    display: 'flex', gap: 8, padding: 10,
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  input: {
    flex: 1, padding: '10px 14px', borderRadius: 10, fontSize: 14,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#f1f5f9', outline: 'none',
  },
  sendBtn: {
    padding: '0 16px', borderRadius: 10, cursor: 'pointer',
    background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)', border: 'none',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
